import React from 'react';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { withTranslation, WithTranslation } from 'react-i18next';
import { compose } from 'redux';
import { connect } from 'react-redux';
import moment from 'moment';
import { notification } from 'antd';
import { FileOutlined } from '@ant-design/icons';
import { Icon, Loader, TableHeader, Button, ColumnsFilterModal, Table as InvTable } from 'react-inv-forms';

import { openProtocolModal, generateGroupProtocols, generateSingleProtocol } from '../../../redux/appConfig/actions';
import { setDataAfterSort, changeColumnsSorting } from '../../../redux/staticViews/actions';

import withCommonTableLogic, { WithCommonTableLogicProps } from '../../../hoc/withCommonTableLogic';
import withTableClickableModalsHandler, {
  WithTableClickableModalsHandlerProps,
} from '../../../hoc/withTableClickableModalsHandler';
import withContextSearch, { WithContextSearchProps } from '../../../hoc/withContextSearch';
import withViewDataSetHandler, { WithViewDataSetHandlerProps } from '../../../hoc/withViewDataSetHandler';
import withDynamicTabLauncher, { WithDynamicTabLauncherProps } from '../../../hoc/withDynamicTabLauncher';
import withTableDataHandler from '../../../hooks/withTableDataHandler';

import Filter from '../../features/Filter';
import DetailsView from '../../features/DetailsView';
import ColumnsAdapter from '../../../helpers/columnsAdapter';
import TableCellTypes from '../../../helpers/tableCellTypes';
import StringBuilder from '../../../helpers/StringBuilder';
import {
  columnsFilterModalTranslations,
  filterTranslations,
  tableDetailsViewTranslations,
} from '../../../settings/i18n/translations/packets';
import { TabConfig } from '../../../interfaces/global';
import { cacheSyncRefreshTime } from '../../../settings';

import DynamicButtonContainer from '../../UI/DynamicButtonContainer';
import CacheInfo from '../../UI/CacheInfo';
import { ContextSearch } from '../../../styles/sharedUIElemStyles';
import { FlexBox, AboveTableContainer, TableContainer, FilterContainer } from '../../../styles/tabContainers';

import filterColumnsModal from '../../../images/filterColumnsModal.svg';

const { generateNonBreakColumns } = ColumnsAdapter;

const Table = withTableDataHandler(InvTable);

type Props = StaticViewComponent &
  MapDispatchToProps &
  RouteComponentProps &
  WithCommonTableLogicProps &
  WithTableClickableModalsHandlerProps &
  WithContextSearchProps &
  WithViewDataSetHandlerProps &
  WithDynamicTabLauncherProps &
  WithTranslation;

/**
 * Główny komponent zakładki Moje Przedmioty zawierający m.in. tabelę z przedmiotami przypisanymi do danego prowadzącego.
 * Wszystkie właściwości komponentu pochodzą ze stanu aplikacji i komponentów wyższego rzędu.
 *
 */
export class MySubjects extends React.Component<Props> {
  componentDidUpdate(): void {
    const { duration, syncInProgress, error } = this.props.state.cache;
    if (moment.now() - duration > cacheSyncRefreshTime && !syncInProgress && !error) {
      this.props.syncDataWithCache();
    }
  }

  /**  Funkcja przetwarzająca wybór opcji filtrującej w modalu */
  onDataFilter = (value: string, filterData: Array<string>): void => {
    this.props.selectFilterValues({
      filterGroupName: value,
      filterValues: filterData,
    });
  };

  /** Funkcja renderująca widok szczegółowy dla zaznaczonego wiersza w tabeli */
  rowDetailsView = (): React.ReactElement | null => {
    const { selected, errorDetails, loadingDetails, columns, userColumnsSetup } = this.props.state;
    if (selected.length === 1) {
      const data = this.props.state.data.filter(item => selected.includes(item.key))[0];
      if (errorDetails) return <span className='block-centered'>{this.props.t('NO_DATA')}</span>;
      else if (loadingDetails) return <Loader style={{ position: 'relative', left: '50%', fontSize: '32px', padding: '20px' }} />;
      else if (data.details) {
        if (this.props.scrollShouldBeFired) {
          this.props.scrollToTableRow(selected[0], 'my_subjects_list_to_display');
        }
        return (
          <DetailsView
            columns={this.props.columnDataWithTranslation(userColumnsSetup.details, columns)}
            data={TableCellTypes.generateTableContent([data.details], columns)}
          >
            {data.details.formyZajec && data.details.formyZajec.czyKierownik ? (
              <>
                <Button outlined={true} onClick={this.generateSingleReport.bind(null, data.details)}>
                  <FileOutlined style={{ lineHeight: '0', marginRight: '8px', fontSize: '18px' }} />
                  {tableDetailsViewTranslations.generateProtocol}
                </Button>
              </>
            ) : null}
          </DetailsView>
        );
      } else return <></>;
    } else return null;
  };

  /** Funkcja wywołująca callback, którego celem jest otwarcie pożądanej zakładki */
  openSubjectTab = ({ przedmiotId, przedmiotNazwa, semestrAkademicki, rokAkademicki, wydzialZlecajacyKod }: TabConfig): void => {
    this.props.openNewTab(
      {
        titleTranslationKey: przedmiotNazwa,
        tabType: 'strona-przedmiotu',
        itemId: `${przedmiotId}_${rokAkademicki}-${semestrAkademicki}_${wydzialZlecajacyKod}`,
      },
      this.props.history,
    );
  };

  /** Funkcja przetwarzająca parametry do pobrania protokołów dla pojedynczego przedmiotu */
  generateSingleReport = ({
    przedmiotKod,
    rokAkademicki,
    semestrAkademicki,
    typPrzydzialu,
    wydzialZlecajacyKod,
  }: TabConfig): void => {
    const poraRoku = StringBuilder.buildSemester(rokAkademicki, semestrAkademicki);
    const kodPrzedmiotu = przedmiotKod.replaceAll(' ', '-');
    const kodWydzialu = wydzialZlecajacyKod;
    this.props.generateSingleProtocol({ kodPrzedmiotu, poraRoku, typPrzydzialu, kodWydzialu });
    this.props.openProtocolModal(poraRoku);
  };

  /** Funkcja przetwarzająca parametry do pobrania protokołów dla wielu przedmiotów. W przypadku braku uprawnień wyświetla notyfikacje */
  generateGroupReport = (): void => {
    const { selected, data } = this.props.state;
    const subjectsIsRights = data
      .filter(item => selected.includes(item.key))
      .filter(subject => subject.formyZajec?.czyKierownik || subject.typPrzydzialu !== 'przydzial_podstawowy');
    if (subjectsIsRights.length) {
      if (subjectsIsRights.length !== selected.length) {
        notification.warn({
          message: this.props.t('GENERATE_PROTOCOL_REMOVED_SUBJECTS_WITH_NO_PERMISSION_TITLE'),
          top: 100,
        });
      }
      const subjectsCodes = subjectsIsRights
        .map(item => ({
          kodPrzedmiotu: item.przedmiotKod.replaceAll(' ', '-'),
          typPrzydzialu: item.typPrzydzialu,
          kodWydzialu: item.wydzialZlecajacyKod,
        }))
        .filter((elem, index, self) => index === self.findIndex(e => e.kodPrzedmiotu === elem.kodPrzedmiotu));
      const poraRoku = StringBuilder.buildSemester(data[0].rokAkademicki, data[0].semestrAkademicki);
      this.props.generateGroupProtocols({ poraRoku, protokoly: subjectsCodes });
      this.props.openProtocolModal(poraRoku);
    } else {
      notification.error({
        message: this.props.t('GENERATE_PROTOCOL_NO_PERMISSION_TO_ANY_SUBJECT'),
        top: 100,
      });
    }
  };

  render(): React.ReactElement {
    const { selected, totalRecords, loading, search, cache, data, columns, userColumnsSetup, filters } = this.props.state;
    return (
      <div>
        <AboveTableContainer>
          <FlexBox justify='flex-end'>
            <ContextSearch
              onChange={this.props.onFrontSearchChange.bind(null, this.props.setSearch)}
              defaultValue={search}
              disabled={loading}
              placeholder={this.props.t('START_TYPING_SHORT')}
              data-cy='contextSearch_mySubjects'
            />
          </FlexBox>
          <FilterContainer justify='flex-start'>
            <div>
              {data.length > 0 ? (
                this.props.state.selected.length === this.props.state.data.length ? (
                  <Button onClick={() => this.props.setSelected([])} data-cy='btnSelectAllSubjects'>
                    {this.props.t('DESELECT_ALL_SUBJECTS')}
                  </Button>
                ) : (
                  <Button
                    outlined
                    onClick={() => this.props.setSelected(this.props.state.data.map(item => item.key))}
                    data-cy='btnSelectAllSubjects'
                  >
                    {this.props.t('SELECT_ALL_SUBJECTS')}
                  </Button>
                )
              ) : null}
            </div>
            <Filter
              filterGroups={this.props.convertDataForFilters(filters.allFilters)}
              onFilterSelection={this.props.openFilterCategory}
              onFilterChange={this.onDataFilter}
              onFilterDisable={this.props.closeFilterCategory}
              translations={filterTranslations}
              loadingFilterList={filters.loading}
            />
            {selected.length >= 2 && (
              <div style={{ marginLeft: 'auto' }}>
                <DynamicButtonContainer
                  onMarksSigning={() => {}}
                  onProtocolGeneration={this.generateGroupReport}
                  translations={tableDetailsViewTranslations}
                  onFinalMarkCopy={() => {}}
                  displayGenerateProtocol
                  canSigning
                />
              </div>
            )}
          </FilterContainer>
          <FlexBox justify='space-between' alignItems='flex-end'>
            <TableHeader data={this.props.generateTableHeaders(totalRecords, this.props.state.data.length, selected.length)} />
            <CacheInfo synInProgress={cache.syncInProgress} isUpToDate={cache.isUpToDate} error={cache.error} />
          </FlexBox>
        </AboveTableContainer>
        <TableContainer loading={loading} data-cy='tableContainer_mySubjects'>
          <Button
            iconButton
            noPadding
            outlined
            disabled={!columns.length || !data.length}
            className='btn_no_hover'
            data-cy='btnColumnFilter_mySujects'
          >
            <Icon
              className={`icon_column-filter-modal ${loading ? 'hidden' : ''}`}
              onClick={columns.length ? this.props.openModal.bind(null, 'filterColumns') : null}
              path={filterColumnsModal}
              dataCy='btnModalColumnsFilterOpen_mySubjects'
            />
          </Button>
          <ColumnsFilterModal
            mainColumns={this.props.convertDataForCheckboxes(columns)}
            detailsColumns={this.props.convertDataForCheckboxes(columns)}
            selectedMainColumns={userColumnsSetup.main}
            selectedDetailColumns={userColumnsSetup.details}
            opened={this.props.openedModals.includes('filterColumns')}
            onFilterConfirmation={this.props.saveColumnsUserSetup}
            onClose={this.props.closeModal}
            translations={columnsFilterModalTranslations}
            onError={() =>
              notification.error({
                message: this.props.t('COLUMNS_FILTER_MODAL_SELECT_MIN_ONE_ERROR'),
                top: 100,
              })
            }
          />
          {data.length ? (
            <Table
              id='my_subjects_list_to_display'
              data-cy='table_mySubjects'
              tableLayout='auto'
              columns={this.props.addFixedColumn(
                this.props.columnDataWithTranslation(userColumnsSetup.main, generateNonBreakColumns(columns)),
              )}
              dataSource={TableCellTypes.generateTableContent(data, columns)}
              search={search}
              searchableColumns={['przedmiotNazwa', 'przedmiotKod', 'kodPrzedmiotuBezSpacji']}
              onRow={(record: any): any => this.props.onSelectedRow(selected, record.key, this.openSubjectTab.bind(null, record))}
              onChange={(pagination: any, filters: any, sorter: any, extra: { currentDataSource: unknown[] }): any => {
                this.props.changeColumnsSorting('MySubjects', { sortOrder: sorter.order, dataIndex: sorter.field });
                if (!sorter.order) {
                  this.props.setDataAfterSort('MySubjects', data);
                }
              }}
              expandable={
                data.length
                  ? this.props.generateExpandableTableSetup(
                      selected,
                      this.rowDetailsView,
                      this.props.getRecordDetails,
                      this.props.setSelected,
                    )
                  : null
              }
              loading={{ spinning: loading, indicator: <Loader style={{ fontSize: '32px' }} /> }}
              scroll={{
                x: 'max-content',
                y: window.outerHeight >= 667 ? 'calc(100vh - 400px)' : '420px',
              }}
            />
          ) : loading ? null : (
            <span className='block-centered'>{this.props.t('NO_DATA')}</span>
          )}
        </TableContainer>
        {loading && !data.length && (
          <FlexBox justify='center'>
            <Loader style={{ fontSize: '32px', padding: '20px' }} />
          </FlexBox>
        )}
      </div>
    );
  }
}

type MapDispatchToProps = ReturnType<typeof mapDispatchToProps>;

const mapDispatchToProps = (dispatch: any) => ({
  generateGroupProtocols: (params: Protocol.ParamsForGroup) => dispatch(generateGroupProtocols(params)),
  openProtocolModal: (poraRoku: Protocol.Basic['poraRoku']) => dispatch(openProtocolModal(poraRoku)),
  generateSingleProtocol: (params: Protocol.ParamsForSingle) => dispatch(generateSingleProtocol(params)),
  setDataAfterSort: (view: StaticView, data: any) => dispatch(setDataAfterSort(view, data)),
  changeColumnsSorting: (view: StaticView, sortingInfo: Helpers.SortingInfo) => dispatch(changeColumnsSorting(view, sortingInfo)),
});

export default compose(
  withTranslation(),
  withRouter,
  connect(null, mapDispatchToProps),
  withCommonTableLogic,
  withTableClickableModalsHandler,
  withContextSearch,
  withViewDataSetHandler,
  withDynamicTabLauncher,
)(MySubjects) as React.ComponentType;
